import React from "react"
import { useDispatch, useSelector } from "react-redux"

import { masterSchemaSlice } from "@/slice/masterSchemaSlice"
import { RootState } from "@/store"
import { gqlClient } from "@/util/graphql"
import { measureText } from "@/util/text"

export const useResizer = (
	tableName: string,
	columnNames: { name: string; display: string }[],
	leftPadding: number,
	rightPadding = 50,
) => {
	const dispatch = useDispatch()

	const tableRef = React.useRef<HTMLDivElement>(null)
	const [resizer, setResizer] = React.useState({
		isGrabResizer: false,
		resizingIndex: 0,
		grabStartX: 0,
	})

	const columnWidth = useSelector((state: RootState) => {
		const tableSchema = state.masterSchema.data.find((s) => s.tableName == tableName)
		if (!tableSchema) throw `not found table schema ${tableName}`
		const columnWidth: { [columnName: string]: number } = {}
		tableSchema.columns.forEach((c) => {
			columnWidth[c.name] = c.displayWidth ?? measureText(c.displayName ?? c.name).width + 36
		})
		return columnWidth
	})

	const tableWidth = () =>
		columnNames.map((c) => columnWidth[c.name]).reduce((sum, v) => (sum += v), leftPadding + rightPadding)
	const gridTemplateColumns = () => `${leftPadding}px ${columnNames.map((c) => columnWidth[c.name] + "px").join(" ")}`

	const startResize = (event: React.MouseEvent, index: number) => {
		setResizer({
			isGrabResizer: true,
			resizingIndex: index,
			grabStartX: event.clientX - columnWidth[columnNames[index].name],
		})
	}

	const finishResize = (_event: React.MouseEvent) => {
		if (resizer.isGrabResizer == true) {
			const name = columnNames[resizer.resizingIndex].name
			const displayWidth = Math.abs(columnWidth[name])
			gqlClient()
				.updateMasterSchema({ tableName, columns: { name, displayWidth } })
				.then(({ updateMasterSchema: { result } }) => {
					if (result) {
						dispatch(masterSchemaSlice.actions.setDisplayWidth({ tableName, columnName: name, displayWidth }))
					}
					setResizer((prev) => ({ ...prev, isGrabResizer: false }))
				})
		}
	}

	const onResize = (event: React.MouseEvent) => {
		if (resizer.isGrabResizer == false) return
		if (tableRef.current == null) return

		const columnName = columnNames[resizer.resizingIndex].name
		dispatch(
			masterSchemaSlice.actions.setDisplayWidth({
				tableName,
				columnName,
				displayWidth: event.clientX - resizer.grabStartX,
			}),
		)

		tableRef.current.style.width = `${tableWidth()}px`
		tableRef.current.style.gridTemplateColumns = gridTemplateColumns()

		event.preventDefault()
	}

	const tableProps = {
		display: "grid",
		width: `${tableWidth()}px`,
		gridTemplateColumns: gridTemplateColumns(),
		paddingRight: `${rightPadding}px`,
	}

	return { startResize, finishResize, onResize, tableProps, tableRef }
}
